import { useState, useMemo, useEffect, useRef, useCallback, Fragment } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useCrud } from '../../api/hooks/useCrud';
import { useStatusDia } from '../../api/hooks/useChamadas';
import { useAlunosTurmaChamada } from '../../api/hooks/useAlunosTurmaChamada';
import { useHorarioAtual } from '../../api/hooks/useHorarios';
import { useHojeReferencia, useSimClock } from '../../api/hooks/useSimClock';
import { useAuthStore } from '../../store/authStore';
import { useChamadaStore } from '../../store/chamadaStore';
import { api } from '../../api/client';
import {
  toMinutes,
  countAlunosNaTurma,
  alunoPertenceTurma,
  AulaDetalheDialog,
  normalizarLabelTurno,
  resolverFaseTurnosPorRelogio,
  combinarFaseTurnoComGrade,
} from '../../components/chamada';
import type { AulaStatus, AulaDetalhePayload, FaseTurnoChamada } from '../../components/chamada';
import type { Turma, Aluno, Materia, StatusPresencaAula, HorarioAula } from '../../types/models';
import '../../components/ui/datatable.css';
import { ChamadaProfessor } from './ChamadaProfessor';
import { DiarioClasseModal } from '../../components/pedagogico/DiarioClasseModal';
import { useDiarioClasse } from '../../api/hooks/useDiarioClasse';
import { ClipboardList, Calendar } from 'lucide-react';
import { ROUTES } from '../../api/routes';

const TURNOS_ORDEM_CHAMADA = ['Manhã', 'Tarde', 'Noite'] as const;

function labelTurnoTurma(t: Turma | undefined, fallback?: string): string {
  const raw =
    (t as any)?.turnoOperacional?.nome ||
    (t as any)?.turno ||
    fallback ||
    '';
  return normalizarLabelTurno(raw);
}

/** Abreviação curta para célula do mapa (código ou iniciais). */
function abbrMateria(nome?: string, codigo?: string | null): string {
  const cod = String(codigo || '').trim();
  if (cod && cod.length <= 6) return cod.toUpperCase();
  const parts = String(nome || '')
    .replace(/[^a-zA-ZÀ-ÿ0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '—';
  if (parts.length === 1) return parts[0].slice(0, 4).toUpperCase();
  return parts
    .map((p) => p[0])
    .join('')
    .slice(0, 4)
    .toUpperCase();
}

function hmShort(hm?: string): string {
  if (!hm || hm.length < 4) return '';
  return hm.slice(0, 5);
}

// Inject mic pulse animation
if (typeof document !== 'undefined' && !document.getElementById('mic-pulse-css')) {
  const style = document.createElement('style');
  style.id = 'mic-pulse-css';
  style.textContent = `@keyframes micPulse { 0% { box-shadow: 0 0 0 0 rgba(220,38,38,0.5); } 70% { box-shadow: 0 0 0 8px rgba(220,38,38,0); } 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0); } }`;
  document.head.appendChild(style);
}

// STATUS_LABELS importado de ../../components/chamada

// AulaStatus importado de ../../components/chamada

// toMinutes e fmtTimer importados de ../../components/chamada

export function ChamadaPage() {
  const user = useAuthStore((s) => s.user);
  const perfil = user?.perfil || 'PROFESSOR';
  const isProfessor = perfil === 'PROFESSOR';
  const { data: turmas } = useCrud<Turma>('turmas');
  const escolaIdStore = useAuthStore((s) => s.escolaId);
  const { data: alunosEscola = [], isLoading: loadingAlunosEscola, refetch: refetchAlunos } = useCrud<Aluno>(
    'alunos',
    undefined,
    { enabled: !isProfessor },
  );
  const { data: materias } = useCrud<Materia>('materias');
  // Admin: buscar TODOS os horários da escola (sem filtro de professor)
  const profIdParam = isProfessor ? user?.id : undefined;
  const { hojeStr, isSimulation } = useHojeReferencia();
  const { virtualEpochMs } = useSimClock({ pollMs: isSimulation ? 2_000 : 15_000 });
  const [dataAtual, setDataAtual] = useState(hojeStr);
  // Na simulação, a data da Central segue o relógio do cockpit.
  useEffect(() => {
    if (isSimulation && hojeStr && hojeStr !== dataAtual) {
      setDataAtual(hojeStr);
    }
  }, [isSimulation, hojeStr, dataAtual]);
  const { horarioAtual, horariosHoje, diaSemana, isLoading: horariosLoading } = useHorarioAtual(profIdParam, dataAtual);
  const queryClient = useQueryClient();

  const { chamadasDia, refetch: refetchStatus } = useStatusDia(profIdParam, dataAtual);

  const [selectedSlot, setSelectedSlot] = useState<HorarioAula | null>(null);
  const [presencas, setPresencas] = useState<Record<string, StatusPresencaAula>>({});
  const [saving, setSaving] = useState(false);
  const [dialogPerdida, setDialogPerdida] = useState<HorarioAula | null>(null);
  const [aulaDetalhe, setAulaDetalhe] = useState<AulaDetalhePayload | null>(null);

  // â”€â”€ Chamada 2.0: fluxo 3 fases â”€â”€
  const [faseAtual, setFaseAtual] = useState<'briefing' | 'operacao' | 'resumo'>('briefing');
  const [alunoIdx, setAlunoIdx] = useState(0);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [chamadaId, setChamadaId] = useState<string | null>(null);
  const setChamadaEmAndamento = useChamadaStore((s) => s.setChamadaEmAndamento);

  // Diário de Classe (pós-chamada)
  const [showDiario, setShowDiario] = useState(false);
  const [diarioMeta, setDiarioMeta] = useState<{
    chamadaId: string; turmaId: string; materiaId: string;
    turmaNome: string; materiaNome: string; dataReferencia: string;
  } | null>(null);
  const { criar: criarDiario } = useDiarioClasse();

  // â”€â”€ RFID: presencas diarias da turma â”€â”€
  const [rfidMap, setRfidMap] = useState<Record<string, { entrou: boolean; horaEntrada?: string }>>({});

  // â”€â”€ Sessão travada: beforeunload â”€â”€
  useEffect(() => {
    const isLocked = faseAtual === 'operacao' || faseAtual === 'resumo';
    setChamadaEmAndamento(isLocked);
    if (!isLocked) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => { window.removeEventListener('beforeunload', handler); setChamadaEmAndamento(false); };
  }, [faseAtual, setChamadaEmAndamento]);

  // â”€â”€ Persistencia localStorage â”€â”€
  const CHAMADA_STORAGE_KEY = 'chamada_sessao_ativa';

  const salvarProgressoLocal = useCallback((extras?: { presencasOverride?: Record<string, StatusPresencaAula>; idxOverride?: number; chamadaIdOverride?: string; slotOverride?: HorarioAula | null }) => {
    const dados = {
      chamadaId: extras?.chamadaIdOverride ?? chamadaId,
      slotId: (extras?.slotOverride !== undefined ? extras.slotOverride?.id : selectedSlot?.id) || '',
      presencas: extras?.presencasOverride ?? presencas,
      alunoIdx: extras?.idxOverride ?? alunoIdx,
      faseAtual,
      timestamp: Date.now(),
    };
    localStorage.setItem(CHAMADA_STORAGE_KEY, JSON.stringify(dados));
  }, [chamadaId, selectedSlot, presencas, alunoIdx, faseAtual]);

  const limparProgressoLocal = useCallback(() => {
    localStorage.removeItem(CHAMADA_STORAGE_KEY);
    localStorage.removeItem('chamada_progresso');
    localStorage.removeItem('chamada_slot_id');
  }, []);

  // Relógio: no briefing só atualiza a cada minuto (evita re-render 1Hz da árvore).
  // Em operação/resumo ticka 1s para countdown.
  const [agora, setAgora] = useState(() =>
    isSimulation ? new Date(virtualEpochMs) : new Date(),
  );
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const precisaSegundos = faseAtual === 'operacao' || faseAtual === 'resumo';
  useEffect(() => {
    const tick = () => {
      const next = isSimulation ? new Date(virtualEpochMs) : new Date();
      setAgora((prev) => {
        if (
          !precisaSegundos &&
          prev.getHours() === next.getHours() &&
          prev.getMinutes() === next.getMinutes()
        ) {
          return prev;
        }
        return next;
      });
    };
    tick();
    const ms = precisaSegundos ? 1_000 : isSimulation ? 2_000 : 15_000;
    timerRef.current = setInterval(tick, ms);
    return () => clearInterval(timerRef.current);
  }, [isSimulation, virtualEpochMs, precisaSegundos]);

  const horaAtual = `${String(agora.getHours()).padStart(2, '0')}:${String(agora.getMinutes()).padStart(2, '0')}`;
  const agoraSegundos = agora.getHours() * 3600 + agora.getMinutes() * 60 + agora.getSeconds();

  // Filtrar aulas, ordenar por hora
  const aulasHoje = useMemo(() =>
    horariosHoje
      .filter((h) => h.tipo === 'AULA')
      .sort((a, b) => (a.horaInicio || '').localeCompare(b.horaInicio || '')),
    [horariosHoje]
  );

  const [contagemPorTurma, setContagemPorTurma] = useState<Record<string, number>>({});
  const [loadingContagemTurmas, setLoadingContagemTurmas] = useState(false);

  useEffect(() => {
    if (!isProfessor || !escolaIdStore || aulasHoje.length === 0) {
      setContagemPorTurma({});
      return;
    }
    const turmaIds = [...new Set(aulasHoje.map((a) => a.turmaId).filter(Boolean))];
    let cancelled = false;
    setLoadingContagemTurmas(true);
    (async () => {
      try {
        const pairs = await Promise.all(
          turmaIds.map(async (tid) => {
            try {
              const { data } = await api.get<{ alunos: Aluno[] }>(
                `/escolas/${escolaIdStore}/chamadas/turma/${tid}/alunos`,
              );
              const alunos = (data?.alunos ?? []) as Aluno[];
              // Reusa na operação (evita 2ª ida à API ao iniciar chamada).
              queryClient.setQueryData(
                ['chamada-alunos-turma', escolaIdStore, tid],
                alunos,
              );
              return [tid, alunos.length] as const;
            } catch {
              return [tid, 0] as const;
            }
          }),
        );
        if (!cancelled) setContagemPorTurma(Object.fromEntries(pairs));
      } finally {
        if (!cancelled) setLoadingContagemTurmas(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isProfessor, escolaIdStore, aulasHoje, queryClient]);

  useEffect(() => {
    if (!isProfessor) refetchAlunos();
  }, [isProfessor, refetchAlunos]);

  const turmaIdAtiva = selectedSlot?.turmaId || '';
  const { alunos: alunosTurmaProfessor = [], isLoading: loadingAlunosTurma } = useAlunosTurmaChamada(
    isProfessor && turmaIdAtiva ? turmaIdAtiva : undefined,
  );

  const alunos = alunosEscola;
  const loadingAlunos = isProfessor
    ? loadingContagemTurmas || loadingAlunosTurma
    : loadingAlunosEscola;

  // Mapa de status — indexa TODAS as chaves (slot id, turma+matéria, turma+nº aula)
  // para o robô/sim e o dashboard não divergirem.
  type ChamadaSlotStatus = {
    status: string;
    totalPresentes: number;
    totalAusentes: number;
    totalAlunos: number;
    chamadaId?: string;
  };
  const statusMap = useMemo(() => {
    const map: Record<string, ChamadaSlotStatus> = {};
    chamadasDia.forEach((c) => {
      const entry: ChamadaSlotStatus = {
        status: c.status,
        totalPresentes: c.totalPresentes,
        totalAusentes: c.totalAusentes,
        totalAlunos: c.totalAlunos ?? 0,
        chamadaId: c.id,
      };
      if (c.horarioAulaId) map[c.horarioAulaId] = entry;
      map[`${c.turmaId}:${c.materiaId ?? ''}`] = entry;
      if (c.numeroAula != null && c.numeroAula > 0) {
        map[`n:${c.turmaId}:${c.numeroAula}`] = entry;
      }
    });
    return map;
  }, [chamadasDia]);

  const chamadaStatusDoSlot = (slot: HorarioAula) =>
    statusMap[slot.id]
    ?? statusMap[`${slot.turmaId}:${slot.materiaId ?? ''}`]
    ?? (slot.numeroAula != null ? statusMap[`n:${slot.turmaId}:${slot.numeroAula}`] : undefined);

  /** No máx. 1 turno AGORA — janela de portões do turno operacional. */
  const faseTurnoPorLabel = useMemo(
    () => resolverFaseTurnosPorRelogio(horaAtual, turmas || []),
    [horaAtual, turmas],
  );

  const faseDoSlot = (slot: HorarioAula): FaseTurnoChamada | null => {
    const turma = turmas?.find((t) => t.id === slot.turmaId);
    const label = labelTurnoTurma(turma);
    return faseTurnoPorLabel.get(label) ?? null;
  };

  /**
   * Alinha com o Dashboard: CONCLUIDA ou ABERTA com lançamento = "feita/registrada".
   * ABERTA vazia = em andamento só no turno vigente.
   */
  function getAulaStatus(slot: HorarioAula): AulaStatus {
    const qtd = isProfessor
      ? (contagemPorTurma[slot.turmaId] ?? 0)
      : countAlunosNaTurma(alunos, slot.turmaId);
    const isVazia = !loadingAlunos && qtd === 0;
    if (isVazia) return 'vazia';
    const chamadaSlot = chamadaStatusDoSlot(slot);
    const st = String(chamadaSlot?.status || '').toUpperCase();
    if (st === 'CONCLUIDA') return 'feita';
    // Espelha AdminDashboardDesktop: ABERTA + alunos = Registrada
    if (st === 'ABERTA' && (chamadaSlot?.totalAlunos ?? 0) > 0) return 'feita';
    if (st === 'ABERTA') return 'agora';

    const faseTurno = faseDoSlot(slot);
    // Turno ainda não abriu: nunca "em aula"/"perdida" por relógio da manhã.
    if (faseTurno === 'futuro') return 'proxima';
    // Turno já fechou sem chamada → perdida (mesmo se o HH:MM da grade for ambíguo).
    if (faseTurno === 'passado') return 'perdida';

    if (horarioAtual?.id === slot.id) return 'agora';
    const ini = slot.horaInicio || '';
    const fim = slot.horaFim || '';
    // Janela meio-aberta [inicio, fim): evita 1ª e 2ª “em aula” no minuto de troca.
    if (fim && horaAtual >= fim) return 'perdida';
    if (ini && fim && horaAtual >= ini && horaAtual < fim) return 'agora';
    if (ini && !fim && horaAtual >= ini) return 'agora';
    return 'proxima';
  }

  // Aula atual para o professor (context-aware)
  const aulaAgora = useMemo(() => {
    return aulasHoje.find((a) => getAulaStatus(a) === 'agora') || null;
  }, [aulasHoje, horarioAtual, statusMap, horaAtual, contagemPorTurma, loadingAlunos]);

  // Proxima aula
  const proximaAula = useMemo(() => {
    return aulasHoje.find((a) => getAulaStatus(a) === 'proxima') || null;
  }, [aulasHoje, horarioAtual, statusMap, horaAtual, contagemPorTurma, loadingAlunos]);

  // KPIs
  const totalFeitas = aulasHoje.filter(a => getAulaStatus(a) === 'feita').length;
  const totalPendentes = aulasHoje.length - totalFeitas;

  // Auto-selecionar aula atual para professor (sem side-effect em useMemo)
  useEffect(() => {
    if (!isProfessor || !aulaAgora || selectedSlot) return;
    const st = getAulaStatus(aulaAgora);
    if (st === 'agora' && !chamadaStatusDoSlot(aulaAgora)) {
      setSelectedSlot(aulaAgora);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só ancora quando a aula atual muda
  }, [aulaAgora?.id, isProfessor, selectedSlot]);

  // Dados da turma selecionada
  const turmaId = selectedSlot?.turmaId || '';
  const materiaId = selectedSlot?.materiaId || '';
  const alunosTurma = isProfessor
    ? alunosTurmaProfessor
    : alunos.filter((a) => alunoPertenceTurma(a, turmaId));
  const turmaAtual = turmas.find((t) => t.id === turmaId);
  const materiaAtual = materias.find((m) => m.id === materiaId);

  // Calculos do timer para a aula selecionada ou aula agora
  const aulaTimer = selectedSlot || aulaAgora;
  const inicioMin = aulaTimer?.horaInicio ? toMinutes(aulaTimer.horaInicio) : 0;
  const fimMin = aulaTimer?.horaFim ? toMinutes(aulaTimer.horaFim) : 0;
  const duracaoTotal = Math.max(1, (fimMin - inicioMin) * 60); // em segundos
  const inicioSec = inicioMin * 60;
  const fimSec = fimMin * 60;
  const decorrido = Math.max(0, agoraSegundos - inicioSec);
  const restante = Math.max(0, fimSec - agoraSegundos);
  const progresso = Math.min(100, Math.max(0, (decorrido / duracaoTotal) * 100));

  const setStatus = (alunoId: string, status: StatusPresencaAula) => {
    setPresencas((prev) => ({ ...prev, [alunoId]: status }));
  };

  const marcarTodos = (status: StatusPresencaAula) => {
    const novo: Record<string, StatusPresencaAula> = {};
    alunosTurma.forEach((a) => { novo[a.id] = status; });
    setPresencas(novo);
  };

  const handleSelecionarAula = (slot: HorarioAula) => {
    const st = getAulaStatus(slot);
    if (st === 'feita') { toast.info('Chamada ja realizada'); return; }
    if (isProfessor && st === 'perdida') { toast.error('Janela expirou. Solicite a coordenacao.'); return; }
    if (isProfessor && st === 'proxima') {
      // Permitir abrir antecipadamente se faltam <= 5 min
      const minParaAula = slot.horaInicio ? toMinutes(slot.horaInicio) - toMinutes(horaAtual) : 999;
      if (minParaAula > 5) {
        toast.info(`Aguarde. Faltam ${minParaAula} min para abrir.`);
        return;
      }
      // Permitido — dentro da janela de 5 min
    }
    setSelectedSlot(slot);
    setPresencas({});
  };

  const handleSalvar = async () => {
    if (!selectedSlot || !turmaId) { toast.error('Selecione uma aula'); return; }
    if (alunosTurma.length === 0) { toast.error('Nenhum aluno nesta turma'); return; }
    const escolaId = useAuthStore.getState().escolaId;
    if (!escolaId) { toast.error('Escola nao identificada'); return; }

    // Se nao tem chamadaId, criar uma (fallback para chamadas sem persistencia)
    let finalChamadaId = chamadaId;

    setSaving(true);
    try {
      if (!finalChamadaId) {
        const { data: chamadaCriada } = await api.post(ROUTES.chamadas.listar(escolaId), {
          turmaId, professorId: user?.id,
          materiaId: materiaId || undefined,
          horarioAulaId: selectedSlot.id || undefined,
          dataReferencia: dataAtual,
        });
        finalChamadaId = chamadaCriada?.id;
        if (!finalChamadaId) { toast.error('Erro: chamada criada sem ID'); setSaving(false); return; }
      }

      const presencasEfetivas = { ...presencas };
      // Se a chamada já veio com JUSTIFICADO (abono), respeita no payload.
      try {
        const { data: detalhe } = await api.get(ROUTES.chamadas.findOne(escolaId, finalChamadaId));
        for (const ca of detalhe?.alunos || []) {
          const aid = ca.alunoId || ca.aluno?.id;
          if (aid && ca.statusPresenca === 'JUSTIFICADO') {
            presencasEfetivas[aid] = 'JUSTIFICADO';
          }
        }
      } catch {
        /* segue com estado local */
      }
      setPresencas(presencasEfetivas);

      await api.post(ROUTES.alunos.listar(escolaId).replace('alunos', `chamadas/${finalChamadaId}/alunos`), {
        alunos: alunosTurma.map((a) => ({
          alunoId: a.id,
          // Backend também força JUSTIFICADO se houver abono ACEITA no dia
          statusPresenca: presencasEfetivas[a.id] || 'PRESENTE',
        })),
      });
      await api.patch(ROUTES.chamadas.concluir(escolaId, finalChamadaId));

      const pCount = alunosTurma.filter((a) => (presencas[a.id] || 'PRESENTE') === 'PRESENTE').length;
      const fCount = alunosTurma.filter((a) => presencas[a.id] === 'AUSENTE').length;
      toast.success(`Chamada salva! ${pCount} presentes, ${fCount} faltas`);

      // Se for professor, mostrar modal de Diário de Classe
      if (isProfessor && finalChamadaId) {
        const tNome = turmaAtual?.nome || turmas.find(t => t.id === turmaId)?.nome || 'Turma';
        const mNome = materiaAtual?.nome || materias.find(m => m.id === materiaId)?.nome || 'Matéria';
        setDiarioMeta({
          chamadaId: finalChamadaId,
          turmaId,
          materiaId: materiaId || '',
          turmaNome: tNome,
          materiaNome: mNome,
          dataReferencia: dataAtual,
        });
        setShowDiario(true);
      }

      // Limpar tudo
      limparProgressoLocal();
      setPresencas({});
      setSelectedSlot(null);
      setChamadaId(null);
      setFaseAtual('briefing');
      setAlunoIdx(0);
      refetchStatus();
      queryClient.invalidateQueries({ queryKey: ['chamadas'] });
      queryClient.invalidateQueries({ queryKey: ['chamadas-status-dia'] });
      queryClient.invalidateQueries({ queryKey: ['horario-atual'] });
      queryClient.invalidateQueries({ queryKey: ['presencas-diarias'] });
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Erro ao salvar chamada';
      toast.error(msg);
    }
    setSaving(false);
  };

  const presentes = alunosTurma.filter((a) => (presencas[a.id] || 'PRESENTE') === 'PRESENTE').length;
  const faltas = alunosTurma.filter((a) => presencas[a.id] === 'AUSENTE').length;
  const atrasados = alunosTurma.filter((a) => presencas[a.id] === 'ATRASADO').length;

  const DIA_LABELS: Record<string, string> = {
    SEGUNDA: 'Segunda-feira', TERCA: 'Ter\u00e7a-feira', QUARTA: 'Quarta-feira',
    QUINTA: 'Quinta-feira', SEXTA: 'Sexta-feira', SABADO: 'S\u00e1bado',
  };

  const STATUS_CONFIG: Record<AulaStatus, { icon: string; label: string; color: string; bg: string }> = {
    vazia: { icon: '⬜', label: 'Vazia', color: 'var(--text-muted)', bg: 'var(--bg-card)' },
    feita:   { icon: '\u2705', label: 'Feita',    color: '#16a34a', bg: '#f0fdf4' },
    agora:   { icon: '\ud83d\udd50', label: 'Agora',    color: '#2563eb', bg: '#eff6ff' },
    pendente:{ icon: '\u23f3', label: 'Pendente', color: '#d97706', bg: '#fffbeb' },
    perdida: { icon: '\u274c', label: 'Perdida',  color: '#dc2626', bg: '#fef2f2' },
    proxima: { icon: '\u23f0', label: 'Pr\u00f3xima', color: '#9ca3af', bg: '#f9fafb' },
  };

  // Agrupar aulas por turno
  const aulasAgrupadas = useMemo(() => {
    const turnoMap = new Map<string, typeof aulasHoje>();
    for (const aula of aulasHoje) {
      const turnoNome = (aula as any).turma?.turnoOperacional?.nome
        || turmas.find((t) => t.id === aula.turmaId)?.turnoOperacional?.nome
        || 'Geral';
      if (!turnoMap.has(turnoNome)) turnoMap.set(turnoNome, []);
      turnoMap.get(turnoNome)!.push(aula);
    }
    return Array.from(turnoMap, ([turno, aulas]) => ({ turno, aulas }));
  }, [aulasHoje, turmas]);

  // â”€â”€â”€ Nome da materia/turma da aula atual â”€â”€â”€
  const materiaAulaAgora = aulaAgora?.materia?.nome || materias.find(m => m.id === aulaAgora?.materiaId)?.nome || '';
  const turmaAulaAgora = turmas.find(t => t.id === aulaAgora?.turmaId);

  // â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• 
  //  RENDER: PROFESSOR (focado, sem lista de aulas)
  // â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• 
  // Handler para fechar o modal de diário (pular ou após salvar)
  const fecharDiario = () => {
    setShowDiario(false);
    setDiarioMeta(null);
  };

  if (isProfessor) {
    return (
      <>
        <ChamadaProfessor
          user={user} aulaAgora={aulaAgora} proximaAula={proximaAula}
          aulasHoje={aulasHoje} statusMap={statusMap} turmas={turmas}
          alunosTurma={alunosTurma} materiaAulaAgora={materiaAulaAgora}
          turmaAulaAgora={turmaAulaAgora} selectedSlot={selectedSlot}
          setSelectedSlot={setSelectedSlot} presencas={presencas}
          setPresencas={setPresencas as any} faseAtual={faseAtual}
          setFaseAtual={setFaseAtual} alunoIdx={alunoIdx}
          setAlunoIdx={setAlunoIdx} chamadaId={chamadaId}
          setChamadaId={setChamadaId} rfidMap={rfidMap}
          setRfidMap={setRfidMap} dataAtual={dataAtual}
          setDataAtual={setDataAtual}
          diaSemana={diaSemana || ''} agoraSegundos={agoraSegundos}
          restante={restante} progresso={progresso}
          chamadasDia={chamadasDia} handleSalvar={handleSalvar}
          saving={saving} salvarProgressoLocal={salvarProgressoLocal}
          limparProgressoLocal={limparProgressoLocal}
          CHAMADA_STORAGE_KEY={CHAMADA_STORAGE_KEY}
          presentes={presentes} faltas={faltas} atrasados={atrasados}
          getAulaStatus={getAulaStatus}
          dialogPerdida={dialogPerdida} setDialogPerdida={setDialogPerdida}
          cancelDialog={cancelDialog} setCancelDialog={setCancelDialog}
          loadingAlunos={loadingAlunos}
          contagemPorTurma={contagemPorTurma}
        />
        {/* Modal Diário de Classe (pós-chamada) */}
        {showDiario && diarioMeta && (
          <DiarioClasseModal
            chamadaId={diarioMeta.chamadaId}
            professorId={user?.id || ''}
            turmaId={diarioMeta.turmaId}
            materiaId={diarioMeta.materiaId}
            turmaNome={diarioMeta.turmaNome}
            materiaNome={diarioMeta.materiaNome}
            dataReferencia={diarioMeta.dataReferencia}
            onSalvar={(dados) => {
              criarDiario.mutate(dados, {
                onSuccess: () => {
                  toast.success('Diário de classe registrado!');
                  fecharDiario();
                },
                onError: () => {
                  toast.error('Erro ao salvar diário. Tente novamente.');
                },
              });
            }}
            onPular={fecharDiario}
            salvando={criarDiario.isPending}
          />
        )}
      </>
    );
  }

  //  RENDER: ADMIN / COORDENACAO — CENTRAL DE GUERRA
  // ══════════════════════════════════════════════════
  const formatDateSafe = (dateStr: string) => {
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10) - 1;
        const d = parseInt(parts[2], 10);
        const date = new Date(y, m, d, 12, 0, 0);
        if (!isNaN(date.getTime())) return date.toLocaleDateString('pt-BR');
      }
      const fallback = new Date(dateStr);
      if (!isNaN(fallback.getTime())) return fallback.toLocaleDateString('pt-BR');
    } catch {}
    return '';
  };
  const dataLegivel = formatDateSafe(dataAtual);
  const diaLegivel = diaSemana ? DIA_LABELS[diaSemana] || diaSemana : '';

  // Agrupar horários por turma para a matriz
  const matrizTurmas = useMemo(() => {
    const turmaMap = new Map<string, {
      turma: Turma;
      aulas: (HorarioAula & { status: AulaStatus; chamadaData?: any })[];
      feitas: number;
      total: number;
      totalPresentes: number;
      totalAlunos: number;
      matriculados: number;
    }>();

    for (const aula of aulasHoje) {
      const t = turmas.find(t => t.id === aula.turmaId);
      if (!t) continue;
      if (!turmaMap.has(t.id)) {
        turmaMap.set(t.id, {
          turma: t,
          aulas: [],
          feitas: 0,
          total: 0,
          totalPresentes: 0,
          totalAlunos: 0,
          matriculados: countAlunosNaTurma(alunos, t.id),
        });
      }
      const grupo = turmaMap.get(t.id)!;
      const st = getAulaStatus(aula);
      const chamadaInfo = chamadaStatusDoSlot(aula);
      grupo.aulas.push({ ...aula, status: st, chamadaData: chamadaInfo } as any);
    }

    // No máximo 1 “em aula” por turma; demais viram perdida/próxima conforme o relógio.
    for (const grupo of turmaMap.values()) {
      const agoraList = grupo.aulas.filter((a) => (a as any).status === 'agora');
      if (agoraList.length > 1) {
        const keep =
          agoraList.find((a) => {
            const ini = a.horaInicio || '';
            const fim = a.horaFim || '';
            return ini && fim && horaAtual >= ini && horaAtual < fim;
          }) || agoraList[0];
        for (const a of agoraList) {
          if (a.id === keep.id) continue;
          const fim = a.horaFim || '';
          (a as any).status = fim && horaAtual >= fim ? 'perdida' : 'proxima';
        }
      }
      grupo.aulas.sort(
        (a, b) => (a.numeroAula || 0) - (b.numeroAula || 0) || (a.horaInicio || '').localeCompare(b.horaInicio || ''),
      );
      grupo.total = grupo.aulas.length;
      grupo.feitas = 0;
      grupo.totalPresentes = 0;
      grupo.totalAlunos = 0;
      for (const a of grupo.aulas) {
        if ((a as any).status === 'feita') {
          grupo.feitas++;
          const c = (a as any).chamadaData;
          grupo.totalPresentes += c?.totalPresentes || 0;
          grupo.totalAlunos += (c?.totalPresentes || 0) + (c?.totalAusentes || 0);
        }
      }
    }

    return Array.from(turmaMap.values()).sort((a, b) =>
      a.turma.nome.localeCompare(b.turma.nome, 'pt-BR'),
    );
  }, [aulasHoje, turmas, statusMap, horaAtual, alunos, loadingAlunos, faseTurnoPorLabel]);

  // Escola completa do dia, seccionada por turno (evita misturar Manhã/Tarde).
  const matrizPorTurno = useMemo(() => {
    const map = new Map<string, typeof matrizTurmas>();
    for (const g of matrizTurmas) {
      const turno = labelTurnoTurma(g.turma);
      if (!map.has(turno)) map.set(turno, []);
      map.get(turno)!.push(g);
    }
    const keys = [...map.keys()].sort((a, b) => {
      const ia = TURNOS_ORDEM_CHAMADA.indexOf(a as (typeof TURNOS_ORDEM_CHAMADA)[number]);
      const ib = TURNOS_ORDEM_CHAMADA.indexOf(b as (typeof TURNOS_ORDEM_CHAMADA)[number]);
      if (ia === -1 && ib === -1) return a.localeCompare(b, 'pt-BR');
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
    const blocos = keys.map((turno) => {
      const turmasBloco = map.get(turno) || [];
      let feitas = 0;
      let total = 0;
      let emAula = 0;
      let proximas = 0;
      let perdidas = 0;
      let vazias = 0;
      for (const g of turmasBloco) {
        for (const a of g.aulas) {
          total++;
          const st = (a as { status?: AulaStatus }).status;
          if (st === 'feita') feitas++;
          else if (st === 'agora') emAula++;
          else if (st === 'proxima' || st === 'pendente') proximas++;
          else if (st === 'perdida') perdidas++;
          else if (st === 'vazia') vazias++;
          else proximas++;
        }
      }
      const faseRelogio = faseTurnoPorLabel.has(turno)
        ? faseTurnoPorLabel.get(turno)
        : undefined;
      const fase = combinarFaseTurnoComGrade(faseRelogio, {
        total,
        feitas,
        emAula,
        proximas,
        perdidas,
        vazias,
      });

      return {
        turno,
        turmas: turmasBloco,
        feitas,
        total,
        emAula,
        proximas,
        perdidas,
        vazias,
        fase,
      };
    });

    // No máximo 1 badge AGORA: prioriza janela do relógio, senão o 1º com aula em curso.
    const agoraRelogio = [...faseTurnoPorLabel.entries()].find(([, f]) => f === 'agora')?.[0];
    const comAgora = blocos.filter((b) => b.fase === 'agora');
    if (comAgora.length > 1) {
      const keep =
        (agoraRelogio && comAgora.find((b) => b.turno === agoraRelogio)) ||
        comAgora.find((b) => b.emAula > 0) ||
        comAgora[0];
      for (const b of comAgora) {
        if (b.turno === keep.turno) continue;
        b.fase =
          b.feitas + b.perdidas + b.vazias === b.total ? 'passado' : 'futuro';
      }
    }

    return blocos;
  }, [matrizTurmas, faseTurnoPorLabel]);

  // KPIs — partição exclusiva: previstas = concluídas + em aula + a iniciar + perdidas (+ vazias)
  const kpiTotal = matrizTurmas.reduce((s, g) => s + g.total, 0);
  const kpiFeitas = matrizTurmas.reduce((s, g) => s + g.feitas, 0);
  const kpiEmAula = matrizPorTurno.reduce((s, b) => s + b.emAula, 0);
  const kpiAIniciar = matrizPorTurno.reduce((s, b) => s + b.proximas, 0);
  const kpiPerdidas = matrizPorTurno.reduce((s, b) => s + b.perdidas, 0);
  const kpiVazias = matrizPorTurno.reduce((s, b) => s + b.vazias, 0);
  const kpiSomaPartes = kpiFeitas + kpiEmAula + kpiAIniciar + kpiPerdidas + kpiVazias;
  const kpiIntegro = kpiTotal === 0 || kpiSomaPartes === kpiTotal;
  // Aderência só sobre o que já deveria ter fechado (concluída vs perdida)
  const kpiEncerraveis = kpiFeitas + kpiPerdidas;
  const kpiAderencia =
    kpiEncerraveis > 0 ? Math.round((kpiFeitas / kpiEncerraveis) * 100) : null;
  const kpiAlunosChamada = matrizTurmas.reduce((s, g) => s + g.totalAlunos, 0);
  const kpiPresentesChamada = matrizTurmas.reduce((s, g) => s + g.totalPresentes, 0);
  const kpiPresenca =
    kpiAlunosChamada > 0
      ? Math.round((kpiPresentesChamada / kpiAlunosChamada) * 100)
      : null;

  const faseTurnoLabel = (fase: 'passado' | 'agora' | 'futuro') =>
    fase === 'passado' ? 'Feito' : fase === 'agora' ? 'Agora' : 'Futuro';

  // Máximo de aulas numa turma (para colunas)
  const maxAulas = Math.max(1, ...matrizTurmas.map(g => Math.max(g.aulas.length, ...g.aulas.map(a => a.numeroAula || 0))));

  /** Cabeçalho da coluna: Nº + horário modal (mais comum na escola). */
  const colunasAulaMeta = useMemo(() => {
    return Array.from({ length: maxAulas }, (_, i) => {
      const n = i + 1;
      const slots = aulasHoje.filter((a) => (a.numeroAula || 0) === n && a.horaInicio);
      const freq = new Map<string, number>();
      for (const s of slots) {
        const key = `${hmShort(s.horaInicio)}-${hmShort(s.horaFim)}`;
        freq.set(key, (freq.get(key) || 0) + 1);
      }
      let best = '';
      let bestN = 0;
      for (const [k, c] of freq) {
        if (c > bestN) {
          best = k;
          bestN = c;
        }
      }
      const [ini, fim] = best ? best.split('-') : ['', ''];
      return { n, ini, fim, label: fim ? `${ini}–${fim}` : ini || `${n}ª` };
    });
  }, [aulasHoje, maxAulas]);

  // Status cell colors
  const cellStyle = (st: AulaStatus): React.CSSProperties => {
    const base: React.CSSProperties = {
      borderRadius: 6, padding: '4px 0', textAlign: 'center', fontSize: '.68rem',
      fontWeight: 600, minWidth: 0, transition: 'all .15s',
    };
    switch (st) {
      case 'feita': return { ...base, background: '#dcfce7', color: '#166534' };
      case 'agora': return { ...base, background: '#dbeafe', color: '#1e40af', boxShadow: '0 0 0 1px #93c5fd' };
      case 'proxima': return { ...base, background: 'var(--bg-main, #f8fafc)', color: '#9ca3af' };
      case 'perdida': return { ...base, background: '#fee2e2', color: '#991b1b' };
      case 'pendente': return { ...base, background: '#fef3c7', color: '#92400e' };
      default: return base;
    }
  };

  const cellLabel: Record<AulaStatus, string> = {
    vazia: 'Vazia',
    feita: '✓', agora: '●', proxima: '—', perdida: '✕', pendente: '…',
  };

  return (
    <div className="admin-page ac-chamada-page" style={{ marginTop: -8, paddingBottom: 24 }}>
      {/* Cabeçalho consolidado: escola + turnos (passado / agora / futuro) */}
      <div className="ac-chamada-head">
        <div className="ac-chamada-head-top">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: '#4f46e5',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ClipboardList size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 className="ac-chamada-head-title">Central de Chamadas</h1>
              <p className="ac-chamada-head-sub">
                {dataLegivel || dataAtual}
                {diaLegivel ? ` · ${diaLegivel}` : ''}
                {' · '}
                {matrizTurmas.length} turmas · {kpiTotal} aulas · escola completa
                {isSimulation ? ' · simulação' : ''}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="date"
              value={dataAtual}
              onChange={(e) => setDataAtual(e.target.value)}
              disabled={isSimulation}
              title={isSimulation ? 'Data sincronizada com o simulador' : 'Escolher data'}
              style={{
                padding: '5px 8px',
                borderRadius: 8,
                border: '1px solid var(--border-color)',
                fontSize: '.78rem',
                background: 'var(--bg-main)',
                color: 'var(--text-main)',
                cursor: isSimulation ? 'not-allowed' : 'pointer',
                outline: 'none',
                opacity: isSimulation ? 0.85 : 1,
              }}
            />
            {isSimulation && (
              <span
                style={{
                  fontSize: '.62rem',
                  fontWeight: 700,
                  color: '#4f46e5',
                  background: '#e0e7ff',
                  padding: '3px 8px',
                  borderRadius: 999,
                }}
              >
                Sim · {horaAtual}
              </span>
            )}
          </div>
        </div>

        <div className="ac-chamada-kpis">
          <span className="ac-chamada-kpi" title="Total de aulas na grade do dia (escola)">
            Previstas <strong>{kpiTotal}</strong>
          </span>
          <span className="ac-chamada-kpi" title="Chamadas CONCLUÍDAS no status-dia">
            Concluídas <strong style={{ color: '#16a34a' }}>{kpiFeitas}</strong>
          </span>
          <span className="ac-chamada-kpi" title="Aulas na janela horária atual (relógio sim/civil)">
            Em aula <strong style={{ color: '#2563eb' }}>{kpiEmAula}</strong>
          </span>
          <span
            className="ac-chamada-kpi"
            title="Aulas que ainda não começaram (não inclui em aula)"
          >
            A iniciar{' '}
            <strong style={{ color: kpiAIniciar > 0 ? '#d97706' : '#16a34a' }}>{kpiAIniciar}</strong>
          </span>
          <span className="ac-chamada-kpi" title="Janela passou sem chamada concluída">
            Perdidas <strong style={{ color: kpiPerdidas > 0 ? '#dc2626' : '#64748b' }}>{kpiPerdidas}</strong>
          </span>
          <span
            className="ac-chamada-kpi"
            title="Concluídas ÷ (concluídas + perdidas). Vazio se nada encerrou ainda."
          >
            Aderência{' '}
            <strong
              style={{
                color:
                  kpiAderencia == null
                    ? '#64748b'
                    : kpiAderencia < 80
                      ? '#dc2626'
                      : kpiAderencia < 100
                        ? '#d97706'
                        : '#16a34a',
              }}
            >
              {kpiAderencia == null ? '—' : `${kpiAderencia}%`}
            </strong>
          </span>
          <span
            className="ac-chamada-kpi"
            title="Presentes ÷ alunos nas chamadas já concluídas (verde = presença)"
          >
            Presença{' '}
            <strong
              style={{
                color:
                  kpiPresenca == null
                    ? '#64748b'
                    : kpiPresenca < 75
                      ? '#dc2626'
                      : kpiPresenca < 85
                        ? '#d97706'
                        : '#16a34a',
              }}
            >
              {kpiPresenca == null ? '—' : `${kpiPresenca}%`}
            </strong>
          </span>
          {!kpiIntegro && (
            <span
              className="ac-chamada-kpi"
              style={{ color: '#b45309', borderColor: '#fde68a', background: '#fffbeb' }}
              title={`Soma das partes (${kpiSomaPartes}) ≠ previstas (${kpiTotal})`}
            >
              Partição {kpiSomaPartes}/{kpiTotal}
            </span>
          )}
          {!diaSemana && (
            <span className="ac-chamada-kpi" style={{ color: '#b45309', borderColor: '#fde68a', background: '#fffbeb' }}>
              Sem grade (fim de semana)
            </span>
          )}
        </div>

        {matrizPorTurno.length > 0 && (
          <div className="ac-chamada-turnos">
            {matrizPorTurno.map((bloco) => (
              <div key={bloco.turno} className={`ac-chamada-turno-chip fase-${bloco.fase}`}>
                <div className="ac-chamada-turno-chip-h">
                  <strong>{bloco.turno}</strong>
                  <span className={`ac-chamada-turno-fase fase-${bloco.fase}`}>
                    {faseTurnoLabel(bloco.fase)}
                  </span>
                </div>
                <div className="ac-chamada-turno-meta">
                  {bloco.turmas.length} turmas · {bloco.feitas}/{bloco.total} ok
                  {bloco.emAula > 0 ? ` · ${bloco.emAula} em aula` : ''}
                  {bloco.perdidas > 0 ? ` · ${bloco.perdidas} perdida(s)` : ''}
                  {bloco.fase === 'futuro' && bloco.proximas > 0 ? ` · ${bloco.proximas} a iniciar` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Matriz de Turmas × Aulas */}
      <div
        className="ac-admin-data-card ac-chamada-mapa-card"
        style={{
          background: 'var(--bg-card, white)',
          border: '1px solid var(--border-color, #e5e7eb)',
          borderRadius: 12,
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', borderBottom: '1px solid var(--border-color, #e5e7eb)',
          background: 'var(--bg-main, #f8fafc)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Calendar size={15} style={{ color: 'var(--text-muted)' }} />
            <span style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--text-main)' }}>
              Mapa de Aulas por Turma · escola completa
            </span>
            <span style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>
              {matrizPorTurno.length} turno(s) · {matrizTurmas.length} turmas · clique na célula = detalhe
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Concluída', bg: '#dcfce7', c: '#166534' },
              { label: 'Em aula', bg: '#dbeafe', c: '#1e40af' },
              { label: 'Próxima', bg: '#f1f5f9', c: '#94a3b8' },
              { label: 'Perdida', bg: '#fee2e2', c: '#991b1b' },
            ].map(l => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: l.bg, border: `1px solid ${l.c}30`, display: 'inline-block' }} />
                <span style={{ fontSize: '.65rem', color: 'var(--text-muted)' }}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="ac-admin-list-scroll" style={{ padding: 0 }}>
          <table className="dt-table ac-chamada-mapa-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--bg-main, #f8fafc)' }}>
              <tr style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '.72rem', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap', width: 200 }}>Turma · Turno</th>
                {colunasAulaMeta.map((col) => (
                  <th
                    key={col.n}
                    style={{ padding: '6px 4px', textAlign: 'center', fontSize: '.68rem', fontWeight: 600, color: 'var(--text-muted)', minWidth: 56 }}
                    title={col.ini && col.fim ? `${col.n}ª aula · ${col.ini}–${col.fim}` : `${col.n}ª aula`}
                  >
                    <div>{col.n}ª</div>
                    {(col.ini || col.fim) && (
                      <div style={{ fontSize: '.55rem', fontWeight: 600, color: '#94a3b8', marginTop: 1, whiteSpace: 'nowrap' }}>
                        {col.ini}{col.fim ? `–${col.fim}` : ''}
                      </div>
                    )}
                  </th>
                ))}
                <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: '.72rem', fontWeight: 600, color: 'var(--text-muted)', width: 80 }} title="Frequência média das chamadas concluídas">
                  Freq.
                </th>
                <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: '.72rem', fontWeight: 600, color: 'var(--text-muted)', width: 88 }} title="Concluídas / total de aulas">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {matrizTurmas.length === 0 ? (
                <tr><td colSpan={maxAulas + 3} style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: '.82rem' }}>
                  {horariosLoading
                    ? 'Carregando…'
                    : !diaSemana
                      ? `Sem aulas em ${dataLegivel || dataAtual} (fim de semana). Na simulação, escolha um dia letivo no cockpit.`
                      : 'Nenhuma turma com aulas nesta data'}
                </td></tr>
              ) : matrizPorTurno.map((bloco) => (
                <Fragment key={`turno-${bloco.turno}`}>
                  <tr style={{ background: bloco.fase === 'agora' ? '#eff6ff' : bloco.fase === 'futuro' ? '#f8fafc' : '#f1f5f9' }}>
                    <td
                      colSpan={maxAulas + 3}
                      style={{
                        padding: '8px 12px',
                        fontWeight: 800,
                        fontSize: '.72rem',
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        color: '#334155',
                      }}
                    >
                      Turno {bloco.turno}
                      <span
                        className={`ac-chamada-turno-fase fase-${bloco.fase}`}
                        style={{ marginLeft: 8, verticalAlign: 'middle' }}
                      >
                        {faseTurnoLabel(bloco.fase)}
                      </span>
                      <span style={{ fontWeight: 600, color: '#64748b', marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>
                        {bloco.turmas.length} turmas · {bloco.feitas}/{bloco.total} aulas
                        {bloco.emAula > 0 ? ` · ${bloco.emAula} em aula` : ''}
                      </span>
                    </td>
                  </tr>
                  {bloco.turmas.map((grupo) => {
                    const pctFreq = grupo.totalAlunos > 0 ? Math.round((grupo.totalPresentes / grupo.totalAlunos) * 100) : null;
                    const allDone = grupo.feitas === grupo.total && grupo.total > 0;
                    const turnoLabel = labelTurnoTurma(grupo.turma, bloco.turno);
                    const emAulaN = grupo.aulas.filter((a) => (a as any).status === 'agora').length;
                    const perdidasN = grupo.aulas.filter((a) => (a as any).status === 'perdida').length;
                    const aulaAgoraTurma = grupo.aulas.find((a) => (a as any).status === 'agora');

                    return (
                      <tr key={grupo.turma.id} style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                          <div style={{ fontWeight: 700, fontSize: '.82rem', color: 'var(--text-main)' }}>{grupo.turma.nome}</div>
                          <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', marginTop: 1 }}>
                            {turnoLabel}
                            {grupo.matriculados > 0 ? ` · ${grupo.matriculados} alunos` : ''}
                          </div>
                          {aulaAgoraTurma && (
                            <div style={{ fontSize: '.62rem', color: '#2563eb', marginTop: 2, fontWeight: 600 }}>
                              Agora:{' '}
                              {abbrMateria(
                                aulaAgoraTurma.materia?.nome ||
                                  materias.find((m) => m.id === aulaAgoraTurma.materiaId)?.nome,
                                (aulaAgoraTurma.materia as any)?.codigo,
                              )}
                              {aulaAgoraTurma.professor?.nome
                                ? ` · ${aulaAgoraTurma.professor.nome.split(' ')[0]}`
                                : ''}
                            </div>
                          )}
                        </td>

                        {Array.from({ length: maxAulas }, (_, i) => {
                          const aula = grupo.aulas.find(a => a.numeroAula === i + 1) || grupo.aulas[i];
                          if (!aula) {
                            return (
                              <td key={i} style={{ padding: '4px 3px', textAlign: 'center' }}>
                                <div style={{ ...cellStyle('proxima'), opacity: 0.25, minHeight: 34 }}> </div>
                              </td>
                            );
                          }

                          const st = (aula as any).status as AulaStatus;
                          const chamadaInfo = (aula as any).chamadaData;
                          const matObj = aula.materia || materias.find((m) => m.id === aula.materiaId);
                          const matNome = matObj?.nome || '';
                          const matAbbr = abbrMateria(matNome, (matObj as any)?.codigo);
                          const profNome = aula.professor?.nome || '—';
                          const nAula = aula.numeroAula || i + 1;
                          const janela = [hmShort(aula.horaInicio), hmShort(aula.horaFim)].filter(Boolean).join('–');

                          let customStyle: React.CSSProperties = {
                            ...cellStyle(st),
                            minHeight: 34,
                            flexDirection: 'column' as const,
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 1,
                            padding: '3px 2px',
                            lineHeight: 1.15,
                          };
                          let content: React.ReactNode = (
                            <>
                              <span style={{ fontWeight: 800, fontSize: '.62rem' }}>{matAbbr}</span>
                              <span style={{ fontSize: '.55rem', opacity: 0.85 }}>{cellLabel[st]}</span>
                            </>
                          );

                          if (st === 'feita' && chamadaInfo) {
                            const t = (chamadaInfo.totalPresentes || 0) + (chamadaInfo.totalAusentes || 0);
                            const pct = t > 0 ? Math.round((chamadaInfo.totalPresentes / t) * 100) : 0;
                            if (pct >= 85) customStyle = { ...customStyle, background: '#dcfce7', color: '#166534' };
                            else if (pct >= 70) customStyle = { ...customStyle, background: '#fef3c7', color: '#92400e' };
                            else customStyle = { ...customStyle, background: '#fee2e2', color: '#991b1b' };
                            content = (
                              <>
                                <span style={{ fontWeight: 800, fontSize: '.66rem' }}>{pct}%</span>
                                <span style={{ fontSize: '.52rem', opacity: 0.85 }}>{matAbbr}</span>
                              </>
                            );
                          }

                          const tip = [
                            `${nAula}ª aula`,
                            janela,
                            matNome || 'Sem matéria',
                            profNome !== '—' ? `Prof. ${profNome}` : 'Sem professor',
                            turnoLabel,
                            st === 'feita' && chamadaInfo
                              ? `${chamadaInfo.totalPresentes}P / ${chamadaInfo.totalAusentes}F`
                              : st,
                          ]
                            .filter(Boolean)
                            .join(' · ');

                          return (
                            <td key={i} style={{ padding: '4px 3px', textAlign: 'center' }}>
                              <button
                                type="button"
                                title={`${tip} · Clique para detalhes`}
                                onClick={() =>
                                  setAulaDetalhe({
                                    aula: aula as HorarioAula & { status: AulaStatus; chamadaData?: any },
                                    turmaNome: grupo.turma.nome,
                                    turnoLabel,
                                    matriculados: grupo.matriculados || 0,
                                    materiaNome: matNome,
                                    professorNome: profNome === '—' ? '' : profNome,
                                    dataReferencia: dataLegivel || dataAtual,
                                  })
                                }
                                style={{
                                  ...customStyle,
                                  width: '100%',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontFamily: 'inherit',
                                }}
                              >
                                {content}
                              </button>
                            </td>
                          );
                        })}

                        <td style={{ padding: '4px 10px', textAlign: 'center' }}>
                          {pctFreq != null ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }} title={`${grupo.totalPresentes} presentes / ${grupo.totalAlunos} nas chamadas`}>
                              <div style={{ height: 5, borderRadius: 3, background: '#e5e7eb', flex: 1, maxWidth: 40, overflow: 'hidden' }}>
                                <div style={{ height: '100%', borderRadius: 3, width: `${pctFreq}%`, background: pctFreq >= 85 ? '#16a34a' : pctFreq >= 70 ? '#d97706' : '#dc2626' }} />
                              </div>
                              <span style={{ fontSize: '.68rem', fontWeight: 600, color: pctFreq >= 85 ? '#16a34a' : pctFreq >= 70 ? '#d97706' : '#dc2626' }}>{pctFreq}%</span>
                            </div>
                          ) : (
                            <span style={{ fontSize: '.65rem', color: 'var(--text-muted)' }} title="Sem chamada concluída nesta turma">
                              —
                            </span>
                          )}
                        </td>

                        <td style={{ padding: '4px 10px', textAlign: 'center' }}>
                          <span
                            style={{
                              fontSize: '.66rem',
                              fontWeight: 600,
                              padding: '2px 6px',
                              borderRadius: 4,
                              background: allDone ? '#dcfce7' : perdidasN > 0 ? '#fee2e2' : emAulaN > 0 ? '#dbeafe' : '#fef3c7',
                              color: allDone ? '#166534' : perdidasN > 0 ? '#991b1b' : emAulaN > 0 ? '#1e40af' : '#92400e',
                            }}
                            title={`${grupo.feitas} concluídas · ${emAulaN} em aula · ${perdidasN} perdidas · ${grupo.total} total`}
                          >
                            {grupo.feitas}/{grupo.total}
                            {emAulaN > 0 ? ` · ${emAulaN}●` : ''}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AulaDetalheDialog detail={aulaDetalhe} onClose={() => setAulaDetalhe(null)} />
    </div>
  );
}

